/**
 * =============================================================================
 * SPEQ55 RFP Pro - Analysis Chat Component
 * =============================================================================
 *
 * Chat interface for asking follow-up questions about an RFP analysis.
 *
 * @module components/analysis/AnalysisChat
 * @author SPEQ55
 */

import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { getChatHistory, sendChatMessage } from '../../api/analysisApi';

function AnalysisChat({ rfpId, analysisId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  /**
   * Load chat history on mount.
   */
  useEffect(() => {
    const loadHistory = async () => {
      setLoading(true);
      try {
        const history = await getChatHistory(rfpId, analysisId);
        setMessages(history);
      } catch {
        setError('Failed to load chat history');
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [rfpId, analysisId]);

  /**
   * Scroll to bottom when messages change.
   */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /**
   * Handle sending a message.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    const question = input.trim();
    if (!question || streaming) return;

    setInput('');
    setError(null);
    setStreaming(true);

    // Add user message immediately
    const userMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: question,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);

    // Add placeholder for assistant response
    const assistantMessage = {
      id: `temp-assistant-${Date.now()}`,
      role: 'assistant',
      content: '',
      createdAt: new Date().toISOString(),
      isStreaming: true,
    };
    setMessages((prev) => [...prev, assistantMessage]);

    try {
      await sendChatMessage(rfpId, analysisId, question, {
        onMessage: (chunk) => {
          setMessages((prev) => {
            const updated = [...prev];
            const lastIndex = updated.length - 1;
            const lastMessage = updated[lastIndex];
            if (lastMessage.role === 'assistant' && lastMessage.isStreaming) {
              // Create new object to avoid mutation
              updated[lastIndex] = {
                ...lastMessage,
                content: lastMessage.content + chunk,
              };
            }
            return updated;
          });
        },
        onDone: () => {
          setMessages((prev) => {
            const updated = [...prev];
            const lastIndex = updated.length - 1;
            const lastMessage = updated[lastIndex];
            if (lastMessage.role === 'assistant') {
              // Create new object to avoid mutation
              updated[lastIndex] = {
                ...lastMessage,
                isStreaming: false,
              };
            }
            return updated;
          });
          setStreaming(false);
          inputRef.current?.focus();
        },
        onError: (err) => {
          setError(err.message || 'Failed to get response');
          setMessages((prev) => prev.filter((m) => !m.isStreaming));
          setStreaming(false);
        },
      });
    } catch (err) {
      setError(err.message || 'Failed to send message');
      setMessages((prev) => prev.filter((m) => !m.isStreaming));
      setStreaming(false);
    }
  };

  /**
   * Format message content with markdown-like styling.
   */
  const formatContent = (content) => {
    // Simple formatting - could be enhanced with a proper markdown parser
    return content.split('\n').map((line, i) => (
      <span key={i}>
        {line}
        {i < content.split('\n').length - 1 && <br />}
      </span>
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-4 mb-4 max-h-96">
        {messages.length === 0 ? (
          <div className="text-center py-8 text-accent-500">
            <p>Ask questions about this RFP analysis.</p>
            <p className="text-sm mt-2">
              Example: &quot;What is the required cake solids content?&quot;
            </p>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] px-4 py-3 rounded-lg ${
                  message.role === 'user'
                    ? 'bg-primary-600 text-white'
                    : 'bg-accent-100 text-accent-800'
                }`}
              >
                <div className="whitespace-pre-wrap text-sm">
                  {formatContent(message.content)}
                  {message.isStreaming && (
                    <span className="inline-block w-2 h-4 ml-1 bg-current animate-pulse" />
                  )}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 px-4 py-2 bg-error-light text-error-dark text-sm rounded-md">
          {error}
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask a question about this RFP..."
          disabled={streaming}
          className="flex-1 px-4 py-2 border border-accent-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={!input.trim() || streaming}
          className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {streaming ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Sending
            </span>
          ) : (
            'Send'
          )}
        </button>
      </form>
    </div>
  );
}

AnalysisChat.propTypes = {
  rfpId: PropTypes.string.isRequired,
  analysisId: PropTypes.string.isRequired,
};

export default AnalysisChat;
